import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState, type ReactNode } from "react";
import { CheckCircle2, Lock, Mail, MessageCircle, Phone, Zap, BadgeCheck } from "lucide-react";
import { dienReclamatieIn, ontgrendelAanvraag, vakmanAanvraag, zetOpvolgstatus } from "@/lib/vakman.functions";
import { VakmanPoort, euro, Melding } from "@/components/vakman";
import { OPVOLG_OPTIES, RECLAMATIE_REDENEN } from "@/lib/vakman-shared";

export const Route = createFileRoute("/vakman/aanvraag/$id")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Aanvraag | Vakmanportaal offerto" },
      { name: "description", content: "Bekijk de details van een offerteaanvraag." },
      { property: "og:title", content: "Aanvraag | Vakmanportaal" },
      { property: "og:description", content: "Details van een offerteaanvraag." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: Pagina,
});

function Pagina() {
  const { id } = Route.useParams();
  return <VakmanPoort>{() => <Detail id={id} />}</VakmanPoort>;
}

function Rij({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[130px_1fr] gap-3 py-1.5 sm:grid-cols-[180px_1fr]">
      <dt className="text-muted-foreground">{label}:</dt>
      <dd className="break-words">{children || "—"}</dd>
    </div>
  );
}

function Blok({ titel, children }: { titel: string; children: ReactNode }) {
  return (
    <section className="border-t border-border py-5">
      <h2 className="mb-2 text-lg font-bold">{titel}</h2>
      {children}
    </section>
  );
}

function telIntl(t: string) {
  const d = t.replace(/\D/g, "");
  return d.startsWith("0") ? `31${d.slice(1)}` : d;
}

function Detail({ id }: { id: string }) {
  const qc = useQueryClient();
  const haal = useServerFn(vakmanAanvraag);
  const ontgrendel = useServerFn(ontgrendelAanvraag);
  const opvolg = useServerFn(zetOpvolgstatus);
  const reclameer = useServerFn(dienReclamatieIn);
  const { data: a, isPending, refetch } = useQuery({ queryKey: ["vakman-aanvraag", id], queryFn: () => haal({ data: { id } }) });
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState("");
  const [rec, setRec] = useState({ open: false, reden: RECLAMATIE_REDENEN[0]!, toelichting: "" });

  if (isPending) return <p className="text-muted-foreground">Laden…</p>;
  if (!a) return <Melding titel="Aanvraag niet beschikbaar">Deze aanvraag valt buiten je werkgebied of bestaat niet meer. <Link to="/vakman" className="underline">Terug naar het overzicht</Link>.</Melding>;

  async function doeOntgrendel() {
    setBezig(true);
    setFout("");
    const res = await ontgrendel({ data: { id } });
    setBezig(false);
    if (!res.ok) return setFout(res.fout ?? "Ontgrendelen mislukt.");
    await qc.invalidateQueries({ queryKey: ["vakman-profiel"] });
    await qc.invalidateQueries({ queryKey: ["vakman-aanvragen"] });
    await refetch();
  }
  async function doeOpvolg(status: "gesproken" | "niet_bereikt") {
    await opvolg({ data: { id, status } });
    await refetch();
  }
  async function doeReclamatie(e: React.FormEvent) {
    e.preventDefault();
    const res = await reclameer({ data: { id, reden: rec.reden, toelichting: rec.toelichting } });
    if (!res.ok) return setFout(res.fout ?? "Indienen mislukt.");
    setRec({ ...rec, open: false });
    await refetch();
  }

  const ontgrendelKnop = (
    <>
      <p className="font-bold">Wil je reageren op deze opdracht?</p>
      {a.saldo < a.prijs ? (
        <Link to="/vakman/tegoed" className="mt-2 flex w-full items-center justify-center gap-2 rounded-md bg-primary px-5 py-3.5 font-bold text-primary-foreground">
          Tegoed te laag – opwaarderen
        </Link>
      ) : (
        <button onClick={() => void doeOntgrendel()} disabled={bezig} className="mt-2 flex w-full items-center justify-center gap-2 rounded-md bg-primary px-5 py-3.5 font-bold text-primary-foreground disabled:opacity-40">
          {bezig ? "Bezig…" : <>Ontgrendel contactgegevens ({euro(a.prijs)}) <CheckCircle2 className="h-5 w-5" /></>}
        </button>
      )}
    </>
  );

  return (
    <div className="space-y-3 pb-28">
      <Link to="/vakman" className="inline-block rounded-md border border-border bg-background px-4 py-2 font-bold">Terug</Link>
      <article className="rounded-xl border border-border bg-background p-5">
        <span className="inline-block rounded-md bg-accent px-3 py-1 text-sm font-bold text-accent-foreground">Offerteaanvraag</span>
        <h1 className="mt-3 text-2xl font-bold leading-tight">
          {a.titel}{a.soort ? ` – ${a.soort.toLowerCase()}` : ""} in {a.plaats} {!a.ontgrendeld && <Lock className="inline h-5 w-5 text-muted-foreground" />}
        </h1>
        <p className="mt-2 text-lg text-muted-foreground">{a.naam} · {euro(a.prijs)}</p>
        <div className="mt-3 flex flex-wrap gap-2 text-sm">
          {!a.ontgrendeld && (
            <span className="flex items-center gap-1 rounded-md border border-border px-2.5 py-1 font-bold"><Zap className="h-4 w-4" />{a.kopers === 0 ? "Ontgrendel als eerste" : `${a.kopers} van 5 vakmannen hebben ontgrendeld`}</span>
          )}
          {a.geverifieerd && <span className="flex items-center gap-1 rounded-md border border-border px-2.5 py-1"><BadgeCheck className="h-4 w-4 text-primary" />Geverifieerd</span>}
        </div>
        {fout && <p className="mt-3 text-sm font-medium text-destructive">{fout}</p>}

        {!a.ontgrendeld && <div className="mt-4">{ontgrendelKnop}</div>}

        {a.ontgrendeld && (
          <div className="mt-4 space-y-2">
            <a href={`mailto:${a.email}`} className="flex w-full items-center justify-center gap-2 rounded-md bg-primary px-5 py-3.5 font-bold text-primary-foreground"><Mail className="h-5 w-5" />Mailen</a>
            <a href={`https://wa.me/${telIntl(a.telefoon)}`} target="_blank" rel="noreferrer" className="flex w-full items-center justify-center gap-2 rounded-md border border-border px-5 py-3.5 font-bold"><MessageCircle className="h-5 w-5" />WhatsApp</a>
            <a href={`tel:${a.telefoon}`} className="flex w-full items-center justify-center gap-2 rounded-md border border-border px-5 py-3.5 font-bold"><Phone className="h-5 w-5" />Bellen</a>
            <p className="flex items-center gap-2 rounded-md bg-secondary px-4 py-3 text-sm"><CheckCircle2 className="h-5 w-5 text-primary" />De contactgegevens zijn nu beschikbaar.</p>
            <div className="rounded-lg bg-secondary p-4">
              <p className="text-sm font-bold uppercase tracking-wide text-primary">Volgende stap</p>
              <p className="mt-1 font-bold">Heb je contact opgenomen met {a.naam}?</p>
              {a.opvolgstatus && <p className="mt-1 text-sm text-muted-foreground">Huidige status: {OPVOLG_OPTIES[a.opvolgstatus as keyof typeof OPVOLG_OPTIES]}</p>}
              <button onClick={() => void doeOpvolg("gesproken")} className="mt-3 w-full rounded-md bg-primary px-5 py-3 font-bold text-primary-foreground">{OPVOLG_OPTIES.gesproken}</button>
              <button onClick={() => void doeOpvolg("niet_bereikt")} className="mt-2 w-full rounded-md px-5 py-3 font-bold">{OPVOLG_OPTIES.niet_bereikt}</button>
            </div>
          </div>
        )}

        <div className="mt-5">
          <Blok titel="Opdracht">
            <dl>
              <Rij label="Categorie">{a.categorie}</Rij>
              <Rij label="Type">{a.titel}</Rij>
              <Rij label="Soort">{a.soort}</Rij>
              <Rij label="Uitvoerdatum">{a.uitvoerdatum}</Rij>
              <Rij label="Omschrijving"><span className="whitespace-pre-line">{a.omschrijving}</span></Rij>
            </dl>
          </Blok>
          <Blok titel="Opvolginfo"><dl><Rij label="Koopintentie">{a.intentie}</Rij></dl></Blok>
          <Blok titel="Contactinformatie">
            <dl>
              <Rij label="Naam">{a.naam}</Rij>
              <Rij label="Adres">{a.adres}</Rij>
              <Rij label="Postcode">{a.postcode}</Rij>
              <Rij label="Plaats">{a.plaats}</Rij>
              <Rij label="Regio">{a.regio}</Rij>
              <Rij label="Telefoon">{a.ontgrendeld ? <a href={`tel:${a.telefoon}`} className="text-primary underline">{a.telefoon}</a> : a.telefoon}</Rij>
              <Rij label="E-mail">{a.ontgrendeld ? <a href={`mailto:${a.email}`} className="text-primary underline">{a.email}</a> : a.email}</Rij>
            </dl>
          </Blok>
          <Blok titel="Foto's">
            {a.fotos.length === 0 ? <p>Er zijn geen foto's toegevoegd.</p> : (
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {a.fotos.map((f) => <a key={f.url} href={f.url} target="_blank" rel="noreferrer"><img src={f.url} alt={f.name} loading="lazy" className="aspect-square w-full rounded-md object-cover" /></a>)}
              </div>
            )}
          </Blok>
          <Blok titel="Offerte details">
            <dl>
              <Rij label="Offerte id">{a.id.slice(0, 8).toUpperCase()}</Rij>
              <Rij label="Datum">{new Date(a.created_at).toLocaleDateString("nl-NL", { day: "numeric", month: "short", year: "numeric" })}</Rij>
              <Rij label="Tijdstip">{new Date(a.created_at).toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" })}</Rij>
              <Rij label="Bron">www.elektricienoffertes.com</Rij>
            </dl>
          </Blok>
          {a.ontgrendeld && (
            <Blok titel="Reclameren">
              {a.reclamatie ? (
                <p>Reclamatie ingediend – status: <strong>{a.reclamatie.status}</strong></p>
              ) : rec.open ? (
                <form onSubmit={(e) => void doeReclamatie(e)} className="space-y-3">
                  <select value={rec.reden} onChange={(e) => setRec({ ...rec, reden: e.target.value })} className="h-11 w-full rounded-md border border-input px-3 text-base">
                    {RECLAMATIE_REDENEN.map((r) => <option key={r}>{r}</option>)}
                  </select>
                  <textarea value={rec.toelichting} onChange={(e) => setRec({ ...rec, toelichting: e.target.value })} placeholder="Toelichting" rows={3} className="w-full rounded-md border border-input p-3 text-base" />
                  <button className="rounded-md bg-primary px-5 py-2.5 font-bold text-primary-foreground">Reclamatie indienen</button>
                </form>
              ) : (
                <button onClick={() => setRec({ ...rec, open: true })} className="font-bold text-primary underline">Reclamatieverzoek indienen</button>
              )}
            </Blok>
          )}
        </div>
      </article>
      <div className="rounded-xl border border-border bg-background p-5">
        <h2 className="text-lg font-bold">Tips</h2>
        <p className="mt-2 text-muted-foreground">Neem binnen 8 uur contact op voor de meeste kans op de opdracht. Kun je de klant niet bereiken? Stuur dan ook een e-mail.</p>
      </div>
      {!a.ontgrendeld && (
        <div className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-background p-3">
          <div className="mx-auto max-w-3xl">{ontgrendelKnop}</div>
        </div>
      )}
    </div>
  );
}
